/**
 * Smart data manager optimized for 2GB devices
 * Handles data loading, caching, and memory management
 */
import localforage from 'localforage';
import { intervalCoordinator } from './intervalCoordinator';
import { compressedStorage } from './compressedStorage';
import { safeSetItem } from './storageQuotaGuard';
import {
  getProductBarcodeSearchText,
  getProductBarcodes,
} from './productBarcodes';

const PRODUCT_INDEX_VERSION = 2;

export class SmartDataManager {
  constructor() {
    this.productsStore = localforage.createInstance({ name: 'Products' });
    this.indexStore = localforage.createInstance({ name: 'ProductIndexes' });
    this.queryCache = new Map(); // Cache for frequent queries
    this.categoryCache = new Map(); // Smart LRU cache for categories
    this.dataCache = new Map();
    this.cacheTimestamps = new Map();
    this.maxCacheAge = 60000; // 1 minute cache
    this.maxMemoryMB = 20; // Limit cache to 20MB for 2GB device
    this.maxCategoryCache = 10; // LRU cache for 10 most recent categories
    this.indexesBuilt = false; // Track if indexes are available
    this.indexBuildingInProgress = false; // Track if currently building
    this.indexStatusListeners = new Set(); // Listeners for index status changes
    this.activeTimers = new Set(); // Track active timers for cleanup
    this.productsCache = null; // In-memory cache for products (loaded once)
    this.productsLoad = null;
    this.productsVersion = 0;

    this.filterResultsCache = new Map();
    this.maxFilterResultsCache = 5;
    this.filterResultsCacheAge = 120000;

    // Register coordinated cleanup interval to prevent conflicts
    intervalCoordinator.registerInterval(
      'SmartDataManager_cleanup',
      () => this.cleanupCache(),
      30000,
      {
        mutex: 'MEMORY_MANAGEMENT',
        priority: 'MEMORY_MANAGEMENT',
        maxExecutionTime: 30,
        deviceScaling: true,
      },
    );
  }

  /**
   * Load persisted indexes from IndexedDB for immediate fast filtering
   */
  async loadPersistedIndexes() {
    try {
      const [
        categoryIndex,
        brandIndex,
        searchIndex,
        priceRangeIndex,
        indexTimestamp,
        indexVersion,
      ] = await Promise.all([
        this.indexStore.getItem('categoryIndex'),
        this.indexStore.getItem('brandIndex'),
        this.indexStore.getItem('searchIndex'),
        this.indexStore.getItem('priceRangeIndex'),
        this.indexStore.getItem('indexTimestamp'),
        this.indexStore.getItem('indexVersion'),
      ]);

      // Check if indexes are recent (less than 24 hours old)
      const isIndexesFresh =
        indexTimestamp &&
        indexVersion === PRODUCT_INDEX_VERSION &&
        Date.now() - indexTimestamp < 24 * 60 * 60 * 1000;

      if (isIndexesFresh && categoryIndex && brandIndex) {
        this.categoryIndex = categoryIndex;
        this.brandIndex = brandIndex;
        this.searchIndex = searchIndex;
        this.priceRangeIndex = priceRangeIndex;
        this.indexesBuilt = true;

        this.devLog(
          `[SmartDataManager] ⚡ Loaded persisted indexes - immediate fast filtering enabled!`,
        );
      } else {
        this.devLog(
          `[SmartDataManager] No valid persisted indexes found, will build on first query`,
        );
      }
    } catch (error) {
      // Failed to load persisted indexes - will build on first query
    }
  }

  dispose() {
    intervalCoordinator.clearInterval('SmartDataManager_cleanup');
    this.queryCache.clear();
    this.categoryCache.clear();
    this.dataCache.clear();
    this.cacheTimestamps.clear();
    this.filterResultsCache.clear();
    this.indexStatusListeners.clear();
    this.indexesBuilt = false;
    this.indexBuildingInProgress = false;
  }

  // Add listener for index building status
  onIndexStatusChange(callback) {
    this.indexStatusListeners.add(callback);
    // Return unsubscribe function
    return () => {
      this.indexStatusListeners.delete(callback);
    };
  }

  // Notify all listeners of status change
  notifyIndexStatus(isBuilding, progress = 0) {
    this.indexBuildingInProgress = isBuilding;
    this.indexStatusListeners.forEach((callback) => {
      try {
        callback(isBuilding, progress);
      } catch (error) {
        // Error in index status listener - skip this callback
      }
    });
  }

  /**
   * Development-only logging to avoid console spam in production
   */
  devLog(message, ...args) {
    // Logging removed for performance optimization
  }

  /**
   * PHASE 3 OPTIMIZATION: Progressive data loading - load critical data first
   */
  async loadCriticalData() {
    const cacheKey = 'critical-data';

    if (this.isCacheValid(cacheKey)) {
      return this.dataCache.get(cacheKey);
    }

    try {
      this.devLog('[SmartDataManager] Loading critical data first...');
      const startTime = performance.now();

      // Load only the most critical data needed for immediate UI display
      const [products, stocks] = await Promise.all([
        this.loadProducts(),
        this.loadStocks(),
      ]);

      const criticalData = {
        products,
        stocks,
        // Provide empty defaults for non-critical data
        productGroups: {},
        deliveryHistory: {},
        productSpecCategories: {},
      };

      this.cacheWithMemoryCheck(cacheKey, criticalData);

      const loadTime = performance.now() - startTime;
      this.devLog(
        `[SmartDataManager] Critical data loaded in ${loadTime.toFixed(1)}ms`,
      );

      // Schedule non-critical data loading in the background
      this.scheduleNonCriticalDataLoading();

      return criticalData;
    } catch (error) {
      return this.getEmptyData();
    }
  }

  /**
   * PHASE 3 OPTIMIZATION: Schedule loading of non-critical data in background
   */
  scheduleNonCriticalDataLoading() {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(
        () => {
          this.loadNonCriticalData();
        },
        { timeout: 5000 },
      );
    } else {
      setTimeout(() => {
        this.loadNonCriticalData();
      }, 500);
    }
  }

  /**
   * PHASE 3 OPTIMIZATION: Load non-critical data in background
   */
  async loadNonCriticalData() {
    try {
      this.devLog(
        '[SmartDataManager] Loading non-critical data in background...',
      );
      const startTime = performance.now();

      const [productGroups, deliveryHistory, productSpecCategories] =
        await Promise.all([
          this.loadProductGroups(),
          this.loadDeliveryHistory(),
          this.loadProductSpecCategories(),
        ]);

      // Update the cached critical data with non-critical data
      const criticalData = this.dataCache.get('critical-data');
      if (criticalData) {
        const completeData = {
          ...criticalData,
          productGroups,
          deliveryHistory,
          productSpecCategories,
        };

        // Cache the complete data and mark as ready
        this.cacheWithMemoryCheck('all-data', completeData);
        this.dataCache.set('non-critical-loaded', true);
      }

      const loadTime = performance.now() - startTime;
      this.devLog(
        `[SmartDataManager] Non-critical data loaded in ${loadTime.toFixed(
          1,
        )}ms`,
      );
    } catch (error) {
      // Error loading non-critical data - continue with critical data only
    }
  }

  /**
   * Load all required data with smart caching and memory management
   */
  async loadAllData() {
    const cacheKey = 'all-data';

    // Check if we have valid cached data
    if (this.isCacheValid(cacheKey)) {
      return this.dataCache.get(cacheKey);
    }

    try {
      // Load data in parallel but handle memory efficiently
      const [
        products,
        stocks,
        productGroups,
        deliveryHistory,
        productSpecCategories,
      ] = await Promise.all([
        this.loadProducts(),
        this.loadStocks(),
        this.loadProductGroups(),
        this.loadDeliveryHistory(),
        this.loadProductSpecCategories(),
      ]);

      const data = {
        products,
        stocks,
        productGroups,
        deliveryHistory,
        productSpecCategories,
      };

      // PHASE 2 OPTIMIZATION: Build indexes in background if needed
      if (products?.length > 1000 && !this.indexesBuilt) {
        // Build indexes in the background without blocking the UI
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => {
            this.buildProductIndexes(products);
          });
        } else {
          setTimeout(() => {
            this.buildProductIndexes(products);
          }, 100);
        }
      }

      // Cache with memory check
      this.cacheWithMemoryCheck(cacheKey, data);
      return data;
    } catch (error) {
      return this.getEmptyData();
    }
  }

  /**
   * Load products with in-memory caching
   */
  async loadProducts() {
    if (this.productsCache) {
      return this.productsCache;
    }

    if (this.productsLoad) return this.productsLoad;
    const version = this.productsVersion;
    const pending = compressedStorage.load(this.productsStore, 'products').then((products) => {
      if (version !== this.productsVersion) return this.loadProducts();
      this.productsCache = Array.isArray(products) ? products : [];
      return this.productsCache;
    }).finally(() => {
      if (this.productsLoad === pending) this.productsLoad = null;
    });
    this.productsLoad = pending;
    return pending;
  }

  /**
   * Set products cache (call this after saving new products)
   */
  setProductsCache(products) {
    this.productsVersion += 1;
    this.productsLoad = null;
    this.productsCache = products;
  }

  /**
   * Clear products cache
   */
  clearProductsCache() {
    this.productsVersion += 1;
    this.productsLoad = null;
    this.productsCache = null;
  }

  /**
   * Load and process stocks data efficiently
   */
  async loadStocks() {
    const cacheKey = 'stocks';
    if (this.isCacheValid(cacheKey)) {
      return this.dataCache.get(cacheKey);
    }

    const stocks = await this.productsStore.getItem('productStocks');
    if (stocks) {
      this.cacheWithMemoryCheck(cacheKey, stocks);
    }
    return stocks || {};
  }

  /**
   * Load product groups with caching
   */
  async loadProductGroups() {
    const cacheKey = 'productGroups';
    if (this.isCacheValid(cacheKey)) {
      return this.dataCache.get(cacheKey);
    }

    const productGroups = await this.productsStore.getItem('productGroups');
    if (productGroups) {
      this.cacheWithMemoryCheck(cacheKey, productGroups);
    }
    return productGroups || {};
  }

  /**
   * Load delivery history with memory management
   */
  async loadDeliveryHistory() {
    const cacheKey = 'deliveryHistory';
    if (this.isCacheValid(cacheKey)) {
      return this.dataCache.get(cacheKey);
    }

    const deliveryHistory = await this.productsStore.getItem('deliveryHistory');
    if (deliveryHistory) {
      // Only cache if not too large
      const estimatedSize = this.estimateObjectSize(deliveryHistory);
      if (estimatedSize < 5) {
        // Less than 5MB
        this.cacheWithMemoryCheck(cacheKey, deliveryHistory);
      }
    }
    return deliveryHistory || {};
  }

  /**
   * Load product spec categories
   */
  async loadProductSpecCategories() {
    const cacheKey = 'productSpecCategories';
    if (this.isCacheValid(cacheKey)) {
      return this.dataCache.get(cacheKey);
    }

    const specCategories = await this.productsStore.getItem(
      'productSpecCategories',
    );
    if (specCategories) {
      this.cacheWithMemoryCheck(cacheKey, specCategories);
    }
    return specCategories || {};
  }

  /**
   * PHASE 2 OPTIMIZATION: Build search and filter indexes for fast queries
   * Now uses chunked processing to prevent UI blocking
   */
  async buildProductIndexes(products) {
    if (!products?.length) return;

    this.devLog(
      `[SmartDataManager] Building indexes for ${products.length} products`,
    );
    const startTime = performance.now();

    // Notify listeners that index building is starting
    this.notifyIndexStatus(true, 0);

    try {
      // Build indexes using chunked processing
      const indexes = await this.buildIndexesChunked(products);

      // Store indexes in memory for immediate use
      this.categoryIndex = indexes.categoryIndex;
      this.brandIndex = indexes.brandIndex;
      this.searchIndex = indexes.searchIndex;
      this.priceRangeIndex = indexes.priceRangeIndex;
      this.indexesBuilt = true;

      // CRITICAL FIX: Persist indexes to IndexedDB for next app load
      await Promise.all([
        safeSetItem(this.indexStore, 'categoryIndex', indexes.categoryIndex),
        safeSetItem(this.indexStore, 'brandIndex', indexes.brandIndex),
        safeSetItem(this.indexStore, 'searchIndex', indexes.searchIndex),
        safeSetItem(this.indexStore, 'priceRangeIndex', indexes.priceRangeIndex),
        safeSetItem(this.indexStore, 'indexTimestamp', Date.now()),
        safeSetItem(this.indexStore, 'indexVersion', PRODUCT_INDEX_VERSION),
      ]);

      const endTime = performance.now();
      this.devLog(
        `[SmartDataManager] Indexes built and persisted in ${Math.round(
          endTime - startTime,
        )}ms`,
      );

      // Notify listeners that index building is complete
      this.notifyIndexStatus(false);
    } catch (error) {
      // Failed to build indexes - notify listeners
      this.notifyIndexStatus(false);
    }
  }

  /**
   * Chunked index building to prevent UI blocking
   */
  async buildIndexesChunked(products) {
    return new Promise((resolve, reject) => {
      const categoryIndex = {};
      const brandIndex = {};
      const searchIndex = {};
      const priceRangeIndex = { low: [], medium: [], high: [] };

      const totalProducts = products.length;
      let processedCount = 0;

      // Chunked processing - 8ms budget per chunk for weak devices
      const CHUNK_SIZE = 500; // Process 500 products per chunk
      const MAX_CHUNK_TIME = 8; // 8ms max per chunk

      const processChunk = () => {
        const chunkStart = performance.now();
        let itemsInChunk = 0;

        while (processedCount < totalProducts && itemsInChunk < CHUNK_SIZE) {
          // Check if we're approaching time limit
          if (
            itemsInChunk > 100 &&
            performance.now() - chunkStart > MAX_CHUNK_TIME
          ) {
            break;
          }

          const product = products[processedCount];
          const {
            id,
            category,
            parentCategory,
            brand,
            name,
            code,
            price,
            retailPrice,
          } = product;

          // Category index
          if (category) {
            if (!categoryIndex[category]) categoryIndex[category] = [];
            categoryIndex[category].push(id);
          }
          if (parentCategory) {
            if (!categoryIndex[parentCategory])
              categoryIndex[parentCategory] = [];
            categoryIndex[parentCategory].push(id);
          }

          // Brand index
          if (brand) {
            if (!brandIndex[brand]) brandIndex[brand] = [];
            brandIndex[brand].push(id);
          }

          // Search index (first 3 characters for fast prefix matching)
          const barcodeSearchTerms = getProductBarcodes(product).flatMap(
            (barcode) => {
              const barcodeLower = barcode.toLowerCase();
              return [
                barcodeLower.substring(0, 3),
                barcodeLower.substring(0, 2),
              ];
            },
          );
          const searchTerms = [
            name?.toLowerCase().substring(0, 3),
            name?.toLowerCase().substring(0, 2),
            code?.toLowerCase().substring(0, 3),
            code?.toLowerCase().substring(0, 2),
            ...barcodeSearchTerms,
          ].filter(Boolean);

          searchTerms.forEach((term) => {
            if (term.length >= 2) {
              if (!searchIndex[term]) searchIndex[term] = [];
              searchIndex[term].push(id);
            }
          });

          // Price range index
          const effectivePrice = price || retailPrice || 0;
          if (effectivePrice < 100) {
            priceRangeIndex.low.push(id);
          } else if (effectivePrice < 1000) {
            priceRangeIndex.medium.push(id);
          } else {
            priceRangeIndex.high.push(id);
          }

          processedCount++;
          itemsInChunk++;
        }

        // Update progress and yield control
        const progress = Math.round((processedCount / totalProducts) * 100);
        this.notifyIndexStatus(true, progress);

        if (processedCount < totalProducts) {
          // Schedule next chunk with setTimeout to yield control
          const timerId = setTimeout(processChunk, 0);
          this.activeTimers.add(timerId);
        } else {
          // All done, resolve with built indexes
          resolve({
            categoryIndex,
            brandIndex,
            searchIndex,
            priceRangeIndex,
          });
        }
      };

      // Start processing
      processChunk();
    });
  }

  /**
   * PHASE 2 OPTIMIZATION: Fast category query with smart caching
   */
  async queryByCategory(category, limit = 100) {
    try {
      // Check smart LRU cache first
      const cacheKey = `cat_${category}_${limit}`;
      if (this.categoryCache.has(cacheKey)) {
        const cached = this.categoryCache.get(cacheKey);
        if (Date.now() - cached.timestamp < 30000) {
          // 30 second cache
          this.devLog(`[SmartDataManager] Category cache hit: ${category}`);
          // Move to end (LRU)
          this.categoryCache.delete(cacheKey);
          this.categoryCache.set(cacheKey, cached);
          return cached.data;
        } else {
          this.categoryCache.delete(cacheKey);
        }
      }

      let results = [];

      // Use in-memory index first if available
      if (this.indexesBuilt && this.categoryIndex) {
        results = await this.queryCategoryFromMemory(category, limit);
        if (results.length > 0) {
          this.addToCategoryCache(cacheKey, results);
          return results;
        }
      }

      // Fallback to direct product filtering with chunked processing
      const { products } = await this.loadAllData();
      if (!products || products.length === 0) {
        return [];
      }

      // Chunked category filtering for better performance
      results = await this.filterCategoryChunked(products, category, limit);
      this.addToCategoryCache(cacheKey, results);

      return results;
    } catch (error) {
      return [];
    }
  }

  /**
   * Ultra-fast category filtering using in-memory indexes
   * This eliminates the need for database lookups
   */
  async queryCategoryFromMemory(category, limit = 1000) {
    if (!this.categoryIndex || !this.categoryIndex[category]) {
      return [];
    }

    // Get product IDs for this category
    const productIds = this.categoryIndex[category];

    // Use existing getProductsByIds method which safely loads products
    const limitedIds = productIds.slice(0, limit);
    return await this.getProductsByIds(limitedIds);
  }

  /**
   * Check if category indexes are ready for fast filtering
   */
  isCategoryIndexReady() {
    return (
      this.indexesBuilt &&
      this.categoryIndex &&
      Object.keys(this.categoryIndex).length > 0
    );
  }

  /**
   * PHASE 2 OPTIMIZATION: Fast brand query using indexes
   */
  async queryByBrand(brand, limit = 100) {
    try {
      // Use in-memory index first (fastest)
      if (this.indexesBuilt && this.brandIndex && this.brandIndex[brand]) {
        const productIds = this.brandIndex[brand].slice(0, limit);
        return await this.getProductsByIds(productIds);
      }

      // Fallback to IndexedDB
      const brandIndex = await this.indexStore.getItem('brandIndex');
      if (!brandIndex || !brandIndex[brand]) return [];

      const productIds = brandIndex[brand].slice(0, limit);
      return await this.getProductsByIds(productIds);
    } catch (error) {
      return [];
    }
  }

  /**
   * PHASE 2 OPTIMIZATION: Fast search using prefix indexes
   */
  async fastSearch(searchTerm, limit = 50) {
    if (!searchTerm || searchTerm.length < 2) return [];

    try {
      let searchIndex = null;

      // Use in-memory index first (fastest)
      if (this.indexesBuilt && this.searchIndex) {
        searchIndex = this.searchIndex;
      } else {
        // Fallback to IndexedDB
        searchIndex = await this.indexStore.getItem('searchIndex');
        if (!searchIndex) return [];
      }

      const prefix = searchTerm.toLowerCase().substring(0, 3);
      const prefix2 = searchTerm.toLowerCase().substring(0, 2);

      // Combine results from different prefix lengths
      const matchingIds = new Set([
        ...(searchIndex[prefix] || []),
        ...(searchIndex[prefix2] || []),
      ]);

      const limitedIds = Array.from(matchingIds).slice(0, limit);
      const products = await this.getProductsByIds(limitedIds);

      // Secondary filter for exact matching on the full search term
      return products.filter((product) => {
        const searchableText = `${product.name || ''} ${
          product.code || ''
        } ${getProductBarcodeSearchText(product)}`.toLowerCase();
        return searchableText.includes(searchTerm.toLowerCase());
      });
    } catch (error) {
      return [];
    }
  }

  /**
   * PHASE 2 OPTIMIZATION: Get products by IDs efficiently
   */
  async getProductsByIds(productIds) {
    if (!productIds?.length) return [];

    try {
      const products = await this.loadProducts();
      if (!products) return [];

      // Create temporary lookup map for O(1) access (not cached)
      const productMap = new Map();
      products.forEach((product) => {
        productMap.set(product.id, product);
      });

      // Return products in the order of requested IDs
      return productIds.map((id) => productMap.get(id)).filter(Boolean);
    } catch (error) {
      return [];
    }
  }

  /**
   * PHASE 2 OPTIMIZATION: Check if indexes are fresh and rebuild if needed
   */
  async ensureIndexesFresh() {
    try {
      const [indexTimestamp, indexVersion, products] = await Promise.all([
        this.indexStore.getItem('indexTimestamp'),
        this.indexStore.getItem('indexVersion'),
        this.loadProducts(),
      ]);

      if (!indexTimestamp || !products) {
        return false;
      }

      // Check if indexes are older than 1 hour or if product count changed
      const indexAge = Date.now() - indexTimestamp;
      const maxAge = 60 * 60 * 1000; // 1 hour

      if (indexAge > maxAge || indexVersion !== PRODUCT_INDEX_VERSION) {
        this.devLog('[SmartDataManager] Indexes are stale, rebuilding...');
        await this.buildProductIndexes(products);
        return true;
      }

      this.indexesBuilt = true;
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * PHASE 2 OPTIMIZATION: Smart query router with non-blocking approach
   */
  async smartQuery(filters, limit = 100) {
    const { category, brands, searchValue } = filters;

    // Try indexed queries first if indexes are ready
    if (this.indexesBuilt) {
      if (searchValue && searchValue.length >= 2) {
        return await this.fastSearch(searchValue, limit);
      }

      if (category) {
        return await this.queryByCategory(category, limit);
      }

      if (brands && brands.length > 0) {
        return await this.queryByBrand(brands[0], limit);
      }
    }

    // Fallback to direct product filtering (no blocking index build)
    const { products } = await this.loadAllData();
    if (!products || products.length === 0) {
      return [];
    }

    // Start building indexes in background if not built yet (non-blocking)
    if (!this.indexesBuilt && !this.indexBuildingInProgress) {
      this.buildProductIndexes(products);
    }

    // Apply filters manually for immediate results
    let filtered = products;

    // Category filter with fast early termination
    if (category) {
      const results = [];
      for (let i = 0; i < products.length && results.length < limit * 3; i++) {
        const product = products[i];
        if (
          product.category === category ||
          product.parentCategory === category
        ) {
          results.push(product);
        }
      }
      filtered = results;
    }

    // Brand filter with early termination
    if (brands && brands.length > 0) {
      const targetBrand = brands[0];
      const results = [];
      for (let i = 0; i < filtered.length && results.length < limit * 3; i++) {
        const product = filtered[i];
        if (product.brand === targetBrand) {
          results.push(product);
        }
      }
      filtered = results;
    }

    // Search filter with early termination
    if (searchValue && searchValue.length >= 2) {
      const searchLower = searchValue.toLowerCase();
      const results = [];
      for (let i = 0; i < filtered.length && results.length < limit * 3; i++) {
        const product = filtered[i];
        const searchText = `${product.name || ''} ${
          product.code || ''
        } ${getProductBarcodeSearchText(product)}`.toLowerCase();
        if (searchText.includes(searchLower)) {
          results.push(product);
        }
      }
      filtered = results;
    }

    return filtered.slice(0, limit);
  }

  /**
   * Process ulistavanje products into efficient map
   */
  processUlistavanjeProducts(ulistavanjeProducts) {
    if (!Array.isArray(ulistavanjeProducts)) return {};

    const cacheKey = `ulistavanje-${ulistavanjeProducts.length}`;
    if (this.isCacheValid(cacheKey)) {
      return this.dataCache.get(cacheKey);
    }

    const map = {};
    ulistavanjeProducts.forEach((item) => {
      if (item?.id) {
        map[item.id] = item;
      }
    });

    this.cacheWithMemoryCheck(cacheKey, map);
    return map;
  }

  /**
   * Check if cached data is still valid
   */
  isCacheValid(key) {
    if (!this.dataCache.has(key)) return false;

    const timestamp = this.cacheTimestamps.get(key);
    if (!timestamp) return false;

    return Date.now() - timestamp < this.maxCacheAge;
  }

  /**
   * Cache data with memory management
   */
  cacheWithMemoryCheck(key, data) {
    const estimatedSize = this.estimateObjectSize(data);
    const currentMemoryUsage = this.estimateCurrentMemoryUsage();

    // Don't cache if it would exceed our memory limit
    if (currentMemoryUsage + estimatedSize > this.maxMemoryMB) {
      this.cleanupCache(); // Try to free some memory
      return;
    }

    if (this.dataCache.size > 50) {
      const keysToDelete = Array.from(this.dataCache.keys()).slice(0, 10);
      keysToDelete.forEach((k) => {
        this.dataCache.delete(k);
        this.cacheTimestamps.delete(k);
      });
    }

    this.dataCache.set(key, data);
    this.cacheTimestamps.set(key, Date.now());
  }

  /**
   * Estimate object size in MB (rough calculation)
   */
  estimateObjectSize(obj) {
    if (!obj) return 0;

    try {
      // Rough estimation: JSON string length * 2 bytes per character / 1024 / 1024
      const jsonStr = JSON.stringify(obj);
      return (jsonStr.length * 2) / (1024 * 1024);
    } catch {
      return 0;
    }
  }

  /**
   * Estimate current memory usage of cache
   */
  estimateCurrentMemoryUsage() {
    let totalSize = 0;
    for (const [, value] of this.dataCache) {
      totalSize += this.estimateObjectSize(value);
    }
    return totalSize;
  }

  /**
   * PHASE 3 OPTIMIZATION: Advanced memory management
   */
  getMemoryUsage() {
    try {
      if (performance.memory) {
        const memory = performance.memory;
        return {
          usedMB: Math.round(memory.usedJSHeapSize / 1024 / 1024),
          totalMB: Math.round(memory.totalJSHeapSize / 1024 / 1024),
          limitMB: Math.round(memory.jsHeapSizeLimit / 1024 / 1024),
          percentUsed: Math.round(
            (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100,
          ),
        };
      }
    } catch (e) {
      // Fallback for browsers without memory API
    }

    // Fallback: estimate based on cache size
    const cacheMemory = this.estimateCurrentMemoryUsage();
    return {
      usedMB: cacheMemory,
      totalMB: cacheMemory,
      limitMB: this.maxMemoryMB,
      percentUsed: Math.round((cacheMemory / this.maxMemoryMB) * 100),
      estimated: true,
    };
  }

  /**
   * Clean up expired cache entries with memory-efficient processing
   */
  async cleanupCache() {
    const startTime = performance.now();
    const maxExecutionTime = 8; // 8ms budget
    const now = Date.now();

    try {
      let deletedCount = 0;
      let processed = 0;
      const maxProcessPerCycle = 12; // Process more items but with time checks

      // Single-pass cleanup to reduce GC pressure
      for (const [key, timestamp] of this.cacheTimestamps) {
        // Time budget check
        if (performance.now() - startTime > maxExecutionTime) break;

        if (now - timestamp > this.maxCacheAge) {
          // Delete immediately to avoid building arrays
          this.dataCache.delete(key);
          this.cacheTimestamps.delete(key);
          deletedCount++;
        }

        processed++;
        if (processed >= maxProcessPerCycle) break;
      }

      // Clean up category cache expired entries
      for (const [key, entry] of this.categoryCache) {
        if (performance.now() - startTime > maxExecutionTime) break;

        if (now - entry.timestamp > 30000) {
          // 30 second expiry
          this.categoryCache.delete(key);
          deletedCount++;
        }
      }

      if (deletedCount > 0) {
        this.devLog(
          `[SmartDataManager] Cleaned up ${deletedCount} cache entries in ${Math.round(
            performance.now() - startTime,
          )}ms`,
        );
      }

      // Memory pressure check
      const memoryInfo = this.getMemoryUsage();
      if (memoryInfo.percentUsed > 85) {
        // Schedule aggressive cleanup during idle time
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => this.emergencyCleanup(), {
            timeout: 500,
          });
        } else {
          setTimeout(() => this.emergencyCleanup(), 50);
        }
      }
    } catch (error) {
      // Cleanup error - skip this cycle
    }
  }

  /**
   * PHASE 3 OPTIMIZATION: Emergency memory cleanup for critical situations
   */
  emergencyCleanup() {
    // Clear all non-essential caches in order of importance
    this.queryCache.clear();
    this.categoryCache.clear();
    this.dataCache.clear();
    this.cacheTimestamps.clear();

    // Reset indexes if memory pressure is severe
    const memoryInfo = this.getMemoryUsage();
    if (memoryInfo.percentUsed > 90) {
      this.indexesBuilt = false;
      this.categoryIndex = null;
      this.brandIndex = null;
      this.searchIndex = null;
      this.priceRangeIndex = null;
      this.devLog(
        '[SmartDataManager] Cleared indexes due to severe memory pressure',
      );
    }

    // Force garbage collection if available (dev tools)
    if (typeof window !== 'undefined' && window.gc) {
      try {
        window.gc();
      } catch (e) {
        // GC not available in production
      }
    }

    const finalMemoryInfo = this.getMemoryUsage();
    this.devLog(
      `[SmartDataManager] Emergency cleanup complete, memory: ${finalMemoryInfo.usedMB}MB (${finalMemoryInfo.percentUsed}%)`,
    );
  }

  /**
   * Get empty data structure
   */
  getEmptyData() {
    return {
      products: [],
      stocks: {},
      productGroups: {},
      deliveryHistory: {},
      productSpecCategories: {},
    };
  }

  /**
   * Add results to smart category cache with LRU eviction
   */
  addToCategoryCache(key, data) {
    // Implement LRU eviction
    if (this.categoryCache.size >= this.maxCategoryCache) {
      const firstKey = this.categoryCache.keys().next().value;
      this.categoryCache.delete(firstKey);
    }

    this.categoryCache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Chunked category filtering to prevent UI blocking
   */
  async filterCategoryChunked(products, category, limit) {
    return new Promise((resolve) => {
      const results = [];
      let processed = 0;
      const chunkSize = 1000;
      const maxTime = 8; // 8ms budget per chunk

      const processChunk = () => {
        const startTime = performance.now();
        const endIndex = Math.min(processed + chunkSize, products.length);

        while (processed < endIndex && results.length < limit) {
          const product = products[processed];
          if (
            product.category === category ||
            product.parentCategory === category
          ) {
            results.push(product);
          }
          processed++;

          // Check time budget every 100 items
          if (
            processed % 100 === 0 &&
            performance.now() - startTime > maxTime
          ) {
            break;
          }
        }

        if (processed < products.length && results.length < limit) {
          // Schedule next chunk
          const timerId = setTimeout(processChunk, 0);
          this.activeTimers.add(timerId);
        } else {
          // Done processing
          resolve(results);
        }
      };

      processChunk();
    });
  }

  /**
   * Clear all caches and free memory
   */
  clearAllCaches() {
    this.dataCache.clear();
    this.cacheTimestamps.clear();
    this.categoryCache.clear();
    this.filterResultsCache.clear();
    this.devLog('[SmartDataManager] All caches cleared');
  }

  /**
   * Get cache statistics for monitoring
   */
  getCacheStats() {
    return {
      cacheEntries: this.dataCache.size,
      categoryCacheEntries: this.categoryCache.size,
      filterResultsCacheEntries: this.filterResultsCache.size,
      estimatedMemoryMB: this.estimateCurrentMemoryUsage(),
      maxMemoryMB: this.maxMemoryMB,
      cacheAge: this.maxCacheAge,
      indexesBuilt: this.indexesBuilt,
    };
  }

  getFilterCacheKey(filters) {
    const {
      orderTypeCode,
      organizationId,
      customerId,
      customersGroup,
      specCategory,
      customer,
      searchValue,
      hasQty,
      ulistan,
      color,
      brandId,
      category,
      customerCategory,
      favourites,
      withoutRecentSales,
      discounted,
      sort,
      agentChannel,
      discountsHash,
      supplierId,
      tobaccoTargetGroup,
      showInAssortment,
    } = filters;

    return JSON.stringify({
      ot: orderTypeCode || '',
      org: organizationId || '',
      cust: customerId || '',
      cg: customersGroup || '',
      sc: specCategory || '',
      c: customer || '',
      s: searchValue || '',
      hq: hasQty || false,
      ul: ulistan || false,
      col: color || false,
      br: brandId || '',
      cat: category || '',
      cc: customerCategory || false,
      fav: favourites || false,
      nrs: withoutRecentSales || false,
      disc: discounted || false,
      sort: sort || 0,
      ch: agentChannel || '',
      dh: discountsHash || 0,
      sup: supplierId || '',
      ttg: tobaccoTargetGroup || '',
      ia: showInAssortment !== false,
    });
  }

  getFilterResultsFromCache(filters) {
    const cacheKey = this.getFilterCacheKey(filters);
    const cached = this.filterResultsCache.get(cacheKey);

    if (!cached) {
      return null;
    }

    if (Date.now() - cached.timestamp > this.filterResultsCacheAge) {
      this.filterResultsCache.delete(cacheKey);
      return null;
    }

    this.filterResultsCache.delete(cacheKey);
    this.filterResultsCache.set(cacheKey, cached);

    return {
      data: cached.data,
      total: cached.total,
    };
  }

  setFilterResultsCache(filters, data, total) {
    const cacheKey = this.getFilterCacheKey(filters);

    if (this.filterResultsCache.size >= this.maxFilterResultsCache) {
      const firstKey = this.filterResultsCache.keys().next().value;
      this.filterResultsCache.delete(firstKey);
    }

    this.filterResultsCache.set(cacheKey, {
      data,
      total,
      timestamp: Date.now(),
    });
  }

  invalidateFilterResultsCache() {
    this.filterResultsCache.clear();
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('msales-products-changed'));
  }

  destroy() {
    if (intervalCoordinator && intervalCoordinator.clearInterval) {
      intervalCoordinator.clearInterval('SmartDataManager_cleanup');
    }

    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }

    if (this.activeTimers) {
      this.activeTimers.forEach((timerId) => {
        clearTimeout(timerId);
      });
      this.activeTimers.clear();
    }

    this.clearAllCaches();

    if (this.queryCache) {
      this.queryCache.clear();
    }

    this.indexesBuilt = false;
    this.categoryIndex = null;
    this.brandIndex = null;
    this.searchIndex = null;
    this.priceRangeIndex = null;

    if (this.indexStatusListeners) {
      this.indexStatusListeners.clear();
    }

    this.devLog('[SmartDataManager] Destroyed - all resources cleaned up');

    try {
      this.productsStore = null;
      this.indexStore = null;
    } catch (error) {}
  }
}

// Export singleton instance
export const smartDataManager = new SmartDataManager();
